import { Id } from "@/fx/Id";
import { enumByIdentifier, enums, items } from "@/fx/data-helpers";
import { CLM } from "./claims";

export namespace SYS {
    export type UserId    = Id & { __resource: "SYS:user"    };
    export type ProcessId = Id & { __resource: "SYS:process" };

    export type ActorId = UserId | ProcessId;

    // #region Action Types

    export type ActionType = {
        identifier: string;
        name:       string;
    };
    export const ActionType = enums({
        Create: { identifier: "Create", name: "Create" },
        Update: { identifier: "Update", name: "Update" },
        Delete: { identifier: "Delete", name: "Delete" },
        Rename: { identifier: "Rename", name: "Rename" },
    });
    export const actionTypes = items<ActionType>(ActionType);
    export const actionType = enumByIdentifier(actionTypes);

    // #endregion

    // #region Resource Types

    export type ResourceType = {
      identifier: string;
      name:       string;
      type:       string;
    }
    export const ResourceType = enums({
      CLAIMS_CLAIM:       { identifier: "CLAIMS_CLAIM",       name: "Claim",       type: "Claims.Claim"       } as ResourceType,
      CLAIMS_DISTRIBUTOR: { identifier: "CLAIMS_DISTRIBUTOR", name: "Distributor", type: "Claims.Distributor" } as ResourceType,
      CLAIMS_PRODUCT:     { identifier: "CLAIMS_PRODUCT",     name: "Product",     type: "Claims.Product"     } as ResourceType,
      SYSTEM_USER:        { identifier: "SYSTEM_USER",        name: "User",        type: "System.User"        } as ResourceType,
      SYSTEM_PROCESS:     { identifier: "SYSTEM_PROCESS",     name: "Process",     type: "System.Process"     } as ResourceType,
    })
    export const resourceTypes = items<ResourceType>(ResourceType);
    export const resourceType  = enumByIdentifier(resourceTypes);

    // #endregion

    // #region Session Status

    export type AuthStatus = {
      identifier: string;
      name:       string;
    }
    export const AuthStatus = enums({
      UNAUTHENTICATED: { identifier: "UNAUTHENTICATED", name: "Unauthenticated" },
      AUTHENTICATED:   { identifier: "AUTHENTICATED",   name: "Authenticated"   },
    })
    export const authStatuses = items<AuthStatus>(AuthStatus);
    export const authStatus  = enumByIdentifier(authStatuses);

    // #region Testing Tags

    export type TestingTag = {
      identifier:     string;
      name:           string;
      shortName:      string;
      resourceTypes: ResourceType[];
    }
    export const TestingTag = enums({
      //Leaving first testing tag as a template for future tags (and because the enum can't be empty, references have been removed)
      DO_NOT_USE_TRACK_SAP_DATA_ENTRY: { identifier: "DO_NOT_USE_TRACK_SAP_DATA_ENTRY", name: "Do Not Use Track SAP Data Entry", resourceTypes: [ResourceType.CLAIMS_CLAIM, ResourceType.SYSTEM_USER], shortName: "SAP Data Entry" } as TestingTag,
    })
    export const testingTags = items<TestingTag>(TestingTag);
    export const testingTag  = enumByIdentifier(testingTags);

    // #endregion
    // #region Test Claims Collection

    export type TestDataCollection = {
      identifier: string;
      name:       string;
      data:       any;
      reset:      boolean;
    }
    export const TestDataCollection = enums({
      TEST_CLAIMS_1:             { identifier: "TEST_CLAIMS_1",             name: "Test Claims Collection 1",  data: { prefix: 21 }, reset: true  } as TestDataCollection,
      TEST_CLAIMS_2:             { identifier: "TEST_CLAIMS_2",             name: "Test Claims Collection 2",  data: { prefix: 22 }, reset: true  } as TestDataCollection,
      OLD_TEST_CLAIMS:           { identifier: "OLD_TEST_CLAIMS",           name: "Old Test Claims",           data: { prefix: 23 }, reset: true  } as TestDataCollection,
      CLAIM_NUMBER_TEST_CLAIMS:  { identifier: "CLAIM_NUMBER_TEST_CLAIMS",  name: "Claim Number Test Claims",  data: { prefix: 11 }, reset: true  } as TestDataCollection,

      TEST_CLAIMS_COMMAND_TESTS: { identifier: "TEST_CLAIMS_COMMAND_TESTS", name: "Test Claims Command Tests", data: { prefix: 20 }, reset: false } as TestDataCollection,

      TEST_CLAIMS_IMPORT_1:      { identifier: "TEST_CLAIMS_IMPORT_1",      name: "Test Claims Import 1",      data: { prefix: 1  }, reset: false } as TestDataCollection,
      TEST_CLAIMS_IMPORT_2:      { identifier: "TEST_CLAIMS_IMPORT_2",      name: "Test Claims Import 2",      data: { prefix: 2  }, reset: false } as TestDataCollection,
      TEST_CLAIMS_IMPORT_3:      { identifier: "TEST_CLAIMS_IMPORT_3",      name: "Test Claims Import 3",      data: { prefix: 3  }, reset: false } as TestDataCollection,
      TEST_CLAIMS_IMPORT_4:      { identifier: "TEST_CLAIMS_IMPORT_4",      name: "Test Claims Import 4",      data: { prefix: 4  }, reset: false } as TestDataCollection,
      TEST_CLAIMS_IMPORT_5:      { identifier: "TEST_CLAIMS_IMPORT_5",      name: "Test Claims Import 5",      data: { prefix: 5  }, reset: false } as TestDataCollection,
      TEST_CLAIMS_IMPORT_6:      { identifier: "TEST_CLAIMS_IMPORT_6",      name: "Test Claims Import 6",      data: { prefix: 6  }, reset: false } as TestDataCollection,
      TEST_CLAIMS_IMPORT_7:      { identifier: "TEST_CLAIMS_IMPORT_7",      name: "Test Claims Import 7",      data: { prefix: 7  }, reset: false } as TestDataCollection,
      TEST_CLAIMS_IMPORT_8:      { identifier: "TEST_CLAIMS_IMPORT_8",      name: "Test Claims Import 8",      data: { prefix: 8  }, reset: false } as TestDataCollection,
      TEST_CLAIMS_IMPORT_9:      { identifier: "TEST_CLAIMS_IMPORT_9",      name: "Test Claims Import 9",      data: { prefix: 9  }, reset: false } as TestDataCollection,
      TEST_CLAIMS_IMPORT_10:     { identifier: "TEST_CLAIMS_IMPORT_10",     name: "Test Claims Import 10",     data: { prefix: 10 }, reset: false } as TestDataCollection,
    })
    export const testDataCollections = items<TestDataCollection>(TestDataCollection);
    export const testDataCollection  = enumByIdentifier(testDataCollections);

    // #endregion
    // #region Actor Reference

    export type ActorReference = {
      id:             SYS.ActorId;
      name:           string;
      managerLevel?:  CLM.ManagerLevel;
    }
    export type RawActorReference = {
      id:            string;
      name:          string;
      managerLevel?: string;
    }

    export const toRawActorReference = (_: ActorReference): RawActorReference => ({
      id:           _.id.asInt.toString(),
      name:         _.name,
      managerLevel: _.managerLevel?.identifier,
    });
    export const fromRawActorReference = (_: RawActorReference): ActorReference => ({
      id:           Id.from(BigInt(_.id)) as SYS.ActorId,
      name:         _.name,
      managerLevel: _.managerLevel ? CLM.managerLevel(_.managerLevel) : undefined,
    });

    export const actorReference = ({ id, name, managerLevel }: { id: string, name: string, managerLevel?: string }): ActorReference => ({
        id:           Id.from(BigInt(id)) as ActorId,
        name:         name,
        managerLevel: managerLevel ? CLM.managerLevel(managerLevel) : undefined,
    });

    // #endregion

    export type HistoricalRecord = {
      at:              Date;
      by:              ActorReference;
      impersonatedBy?: ActorReference;
    }

    export type Info = {
        created:  HistoricalRecord;
        updated:  HistoricalRecord;
        deleted?: HistoricalRecord;
    };

    export type RawSysInfo = {
        sysInfo_createdAt:                    string;
        sysInfo_createdBy_id:                 string;
        sysInfo_createdBy_name:               string;
        sysInfo_created_impersonatedBy_id?:   string;
        sysInfo_created_impersonatedBy_name?: string;
        sysInfo_updatedAt:                    string;
        sysInfo_updatedBy_id:                 string;
        sysInfo_updatedBy_name:               string;
        sysInfo_updated_impersonatedBy_id?:   string;
        sysInfo_updated_impersonatedBy_name?: string;
        sysInfo_deletedAt?:                   string;
        sysInfo_deletedBy_id?:                string;
        sysInfo_deletedBy_name?:              string;
        sysInfo_deleted_impersonatedBy_id?:   string;
        sysInfo_deleted_impersonatedBy_name?: string;
    };

    export function fromRawSysInfo(_: SYS.RawSysInfo): SYS.Info {
      return info({
        createdAt:                   _.sysInfo_createdAt,
        createdBy_id:                _.sysInfo_createdBy_id,
        createdBy_name:              _.sysInfo_createdBy_name,
        created_impersonatedBy_id:   _.sysInfo_created_impersonatedBy_id,
        created_impersonatedBy_name: _.sysInfo_created_impersonatedBy_name,
        updatedAt:                   _.sysInfo_updatedAt,
        updatedBy_id:                _.sysInfo_updatedBy_id,
        updatedBy_name:              _.sysInfo_updatedBy_name,
        updated_impersonatedBy_id:   _.sysInfo_updated_impersonatedBy_id,
        updated_impersonatedBy_name: _.sysInfo_updated_impersonatedBy_name,
        deletedAt:                   _.sysInfo_deletedAt,
        deletedBy_id:                _.sysInfo_deletedBy_id,
        deletedBy_name:              _.sysInfo_deletedBy_name,
        deleted_impersonatedBy_id:   _.sysInfo_deleted_impersonatedBy_id,
        deleted_impersonatedBy_name: _.sysInfo_deleted_impersonatedBy_name,
      });
    }

    export function toRawSysInfo(_: SYS.Info): SYS.RawSysInfo {
        return {
            sysInfo_createdAt:                   _.created.at.toISOString(),
            sysInfo_createdBy_id:                _.created.by.id.asInt.toString(),
            sysInfo_createdBy_name:              _.created.by.name,
            sysInfo_created_impersonatedBy_id:   _.created.impersonatedBy?.id?.asInt.toString(),
            sysInfo_created_impersonatedBy_name: _.created.impersonatedBy?.name,
            sysInfo_updatedAt:                   _.updated.at.toISOString(),
            sysInfo_updatedBy_id:                _.updated.by.id.asInt.toString(),
            sysInfo_updatedBy_name:              _.updated.by.name,
            sysInfo_updated_impersonatedBy_id:   _.updated.impersonatedBy?.id?.asInt.toString(),
            sysInfo_updated_impersonatedBy_name: _.updated.impersonatedBy?.name,
            sysInfo_deletedAt:                   _.deleted?.at.toISOString(),
            sysInfo_deletedBy_id:                _.deleted?.by.id.asInt.toString(),
            sysInfo_deletedBy_name:              _.deleted?.by.name,
            sysInfo_deleted_impersonatedBy_id:   _.deleted?.impersonatedBy?.id?.asInt.toString(),
            sysInfo_deleted_impersonatedBy_name: _.deleted?.impersonatedBy?.name,
        };
    }

    export const info = ({ createdAt, createdBy_id, createdBy_name, created_impersonatedBy_id, created_impersonatedBy_name, updatedAt, updatedBy_id, updatedBy_name, updated_impersonatedBy_id, updated_impersonatedBy_name, deletedAt, deletedBy_id, deletedBy_name, deleted_impersonatedBy_id, deleted_impersonatedBy_name }: {
        createdAt:                    string,
        createdBy_id:                 string,
        createdBy_name:               string,
        created_impersonatedBy_id?:   string,
        created_impersonatedBy_name?: string,
        updatedAt:                    string,
        updatedBy_id:                 string,
        updatedBy_name:               string,
        updated_impersonatedBy_id?:   string,
        updated_impersonatedBy_name?: string,
        deletedAt?:                   string,
        deletedBy_id?:                string,
        deletedBy_name?:              string,
        deleted_impersonatedBy_id?:   string,
        deleted_impersonatedBy_name?: string,
    }): Info => ({
        created: {
            at:             new Date(createdAt),
            by:             actorReference({ id: createdBy_id, name: createdBy_name }),
            impersonatedBy: created_impersonatedBy_id && created_impersonatedBy_name ? actorReference({ id: created_impersonatedBy_id, name: created_impersonatedBy_name }) : undefined,
        },
        updated: {
            at:             new Date(updatedAt),
            by:             actorReference({ id: updatedBy_id, name: updatedBy_name }),
            impersonatedBy: updated_impersonatedBy_id && updated_impersonatedBy_name ? actorReference({ id: updated_impersonatedBy_id, name: updated_impersonatedBy_name }) : undefined,
        },
        deleted: deletedAt && deletedBy_id && deletedBy_name ? {
            at:             new Date(deletedAt),
            by:             actorReference({ id: deletedBy_id, name: deletedBy_name }),
            impersonatedBy: deleted_impersonatedBy_id && deleted_impersonatedBy_name ? actorReference({ id: deleted_impersonatedBy_id, name: deleted_impersonatedBy_name }) : undefined,
        } : undefined,
    });

    export type KeyMoment = HistoricalRecord;

    export type RawKeyMoment = {
        at:                   string;
        by_id:                string;
        by_name:              string;
        by_managerLevel?:     string;
        impersonatedBy_id?:   string;
        impersonatedBy_name?: string;
    };

    export function fromRawKeyMoment(_: SYS.RawKeyMoment): SYS.KeyMoment {
      return keyMoment({
        at:                   _.at,
        by_id:                _.by_id,
        by_name:              _.by_name,
        by_managerLevel:      _.by_managerLevel,
        impersonatedBy_id:    _.impersonatedBy_id,
        impersonatedBy_name:  _.impersonatedBy_name,
      });
    }

    export function toRawKeyMoment(_: SYS.KeyMoment): SYS.RawKeyMoment {
      return {
          at:                  _.at.toISOString(),
          by_id:               _.by.id.asInt.toString(),
          by_name:             _.by.name,
          by_managerLevel:     _.by.managerLevel?.identifier,
          impersonatedBy_id:   _.impersonatedBy?.id?.asInt.toString(),
          impersonatedBy_name: _.impersonatedBy?.name,
      };
    }

    export const keyMoment = ({ at, by_id, by_name, by_managerLevel, impersonatedBy_id, impersonatedBy_name }: { at: string, by_id: string, by_name: string, by_managerLevel?: string, impersonatedBy_id?: string, impersonatedBy_name?: string }): KeyMoment => ({
        at:             new Date(at),
        by:             actorReference({ id: by_id, name: by_name, managerLevel: by_managerLevel }),
        impersonatedBy: impersonatedBy_id && impersonatedBy_name ? actorReference({ id: impersonatedBy_id, name: impersonatedBy_name }) : undefined,
    });

    export const optionalKeyMoment = ({ at, by_id, by_name, by_managerLevel, impersonatedBy_id, impersonatedBy_name }: { at: string | undefined, by_id: string | undefined, by_name: string | undefined, by_managerLevel?: string | undefined, impersonatedBy_id?: string | undefined, impersonatedBy_name?: string | undefined }): KeyMoment | undefined => (
      at && by_id && by_name ? keyMoment({ at, by_id, by_name, by_managerLevel, impersonatedBy_id, impersonatedBy_name }) : undefined
    );
}
